import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, Stethoscope } from "lucide-react";
import {
  fetchAppointments,
  fetchTherapistProfile,
  therapistAction,
  type TherapistAppointment,
} from "../api/therapist";
import { Button, Card, EmptyState, ErrorState, PageHeader, Spinner } from "../components/ui";
import { StatusPill } from "../components/status";
import { formatDateLong, formatDateShort } from "../lib/format";

const DAY_NAMES_SHORT = ["Sen", "Sel", "Rab", "Kam", "Jum", "Sab", "Min"];
const DAY_LABELS = ["Senin", "Selasa", "Rabu", "Kamis", "Jumat", "Sabtu", "Minggu"];

function todayLocal(): string {
  return new Date(Date.now() + 7 * 60 * 60 * 1000).toISOString().slice(0, 10);
}
function shiftDate(dateStr: string, days: number): string {
  const d = new Date(`${dateStr}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}
function dow(dateStr: string): number {
  return (new Date(`${dateStr}T00:00:00Z`).getUTCDay() + 6) % 7;
}

export function TherapistTodayPage() {
  const queryClient = useQueryClient();
  const [date, setDate] = useState(todayLocal());
  const profileQuery = useQuery({ queryKey: ["therapist", "me"], queryFn: fetchTherapistProfile });
  const listQuery = useQuery({
    queryKey: ["therapist-appointments", { date }],
    queryFn: () => fetchAppointments({ date }),
  });

  const actionMutation = useMutation({
    mutationFn: ({ id, action }: { id: string; action: "check-in" | "start" | "complete" }) =>
      therapistAction(id, action),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["therapist-appointments"] }),
  });

  const name = profileQuery.data?.name;

  return (
    <div>
      <PageHeader
        title={name ? `${name} — Hari Ini` : "Jadwal Hari Ini"}
        subtitle={formatDateLong(date)}
        action={
          <div style={{ display: "flex", gap: 8 }}>
            <Button variant="outline" onClick={() => setDate((d) => shiftDate(d, -1))}>
              <ChevronLeft size={16} />
            </Button>
            <Button variant="outline" onClick={() => setDate((d) => shiftDate(d, 1))}>
              <ChevronRight size={16} />
            </Button>
          </div>
        }
      />

      {listQuery.isPending ? (
        <Spinner />
      ) : listQuery.isError ? (
        <ErrorState onRetry={() => void listQuery.refetch()} />
      ) : (listQuery.data?.length ?? 0) === 0 ? (
        <EmptyState title="Belum ada appointment hari ini." hint="Cek tanggal lain atau jadwal mingguan." />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {listQuery.data?.map((a: TherapistAppointment) => (
            <Card key={a.id}>
              <div className="list-row-top">
                <span className="list-row-time">
                  {a.startTime}–{a.endTime}
                </span>
                <StatusPill kind="booking" value={a.status} />
              </div>
              <div className="list-row-main">
                <p>
                  <strong>{a.patientProfile.fullName}</strong>
                  {a.patientProfile.phone ? (
                    <span className="muted-line"> · {a.patientProfile.phone}</span>
                  ) : null}
                </p>
                <p className="muted-line">
                  {a.bookingCode} · {a.service.name}
                </p>
                {a.notes ? <p className="muted-line">Catatan: {a.notes}</p> : null}
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
                {a.status === "CONFIRMED" ? (
                  <Button
                    loading={actionMutation.isPending}
                    onClick={() => actionMutation.mutate({ id: a.id, action: "check-in" })}
                  >
                    Check-in
                  </Button>
                ) : null}
                {a.status === "CHECKED_IN" ? (
                  <Button
                    loading={actionMutation.isPending}
                    onClick={() => actionMutation.mutate({ id: a.id, action: "start" })}
                  >
                    Mulai Sesi
                  </Button>
                ) : null}
                {a.status === "IN_PROGRESS" ? (
                  <Button
                    loading={actionMutation.isPending}
                    onClick={() => actionMutation.mutate({ id: a.id, action: "complete" })}
                  >
                    Selesaikan Sesi
                  </Button>
                ) : null}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

export function TherapistSchedulePage() {
  const weekStart = shiftDate(todayLocal(), -dow(todayLocal()));
  const [from, setFrom] = useState(weekStart);
  const [to, setTo] = useState(shiftDate(weekStart, 6));

  const listQuery = useQuery({
    queryKey: ["therapist-appointments", { from, to }],
    queryFn: () => fetchAppointments({ from, to }),
  });

  function moveWeek(dir: number) {
    const next = shiftDate(from, 7 * dir);
    setFrom(next);
    setTo(shiftDate(next, 6));
  }

  const days = Array.from({ length: 7 }, (_, i) => shiftDate(from, i));
  const byDay = new Map<string, TherapistAppointment[]>();
  for (const a of listQuery.data ?? []) {
    const arr = byDay.get(a.bookingDate) ?? [];
    arr.push(a);
    byDay.set(a.bookingDate, arr);
  }

  return (
    <div>
      <PageHeader
        title="Jadwal Mingguan"
        action={
          <div style={{ display: "flex", gap: 8 }}>
            <Button variant="outline" onClick={() => moveWeek(-1)}>
              <ChevronLeft size={16} /> Minggu lalu
            </Button>
            <Button variant="outline" onClick={() => moveWeek(1)}>
              Minggu depan <ChevronRight size={16} />
            </Button>
          </div>
        }
      />
      {listQuery.isPending ? (
        <Spinner />
      ) : listQuery.isError ? (
        <ErrorState onRetry={() => void listQuery.refetch()} />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {days.map((d) => {
            const items = byDay.get(d) ?? [];
            return (
              <Card key={d}>
                <p style={{ margin: 0, fontWeight: 700 }}>
                  {DAY_LABELS[dow(d)]}, {formatDateShort(d)}
                  <span className="muted-line"> · {items.length} appointment</span>
                </p>
                {items.length === 0 ? (
                  <p className="muted-line" style={{ marginBottom: 0 }}>
                    Tidak ada jadwal.
                  </p>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 8 }}>
                    {items.map((a) => (
                      <div key={a.id} className="summary-row" style={{ borderBottom: "none" }}>
                        <span>
                          <strong>
                            {a.startTime}–{a.endTime}
                          </strong>{" "}
                          · {a.patientProfile.fullName}
                        </span>
                        <StatusPill kind="booking" value={a.status} />
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function TherapistAppointmentsPage() {
  const listQuery = useQuery({
    queryKey: ["therapist-appointments", { range: true }],
    queryFn: () => fetchAppointments({ from: todayLocal(), to: shiftDate(todayLocal(), 30) }),
  });
  return (
    <div>
      <PageHeader title="Semua Appointment" subtitle="30 hari ke depan." />
      {listQuery.isPending ? (
        <Spinner />
      ) : listQuery.isError ? (
        <ErrorState onRetry={() => void listQuery.refetch()} />
      ) : (listQuery.data?.length ?? 0) === 0 ? (
        <EmptyState title="Tidak ada appointment dalam 30 hari." />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {listQuery.data?.map((a: TherapistAppointment) => (
            <Card key={a.id} className="list-row">
              <div className="list-row-top">
                <span className="list-row-time">
                  {DAY_NAMES_SHORT[dow(a.bookingDate)]} {formatDateShort(a.bookingDate)} ·{" "}
                  {a.startTime}
                </span>
                <StatusPill kind="booking" value={a.status} />
              </div>
              <p className="list-row-main" style={{ marginBottom: 0 }}>
                <strong>{a.patientProfile.fullName}</strong> · {a.service.name} ·{" "}
                <Stethoscope size={12} /> {a.bookingCode}
              </p>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
